// frontend/src/queries/api.js
//
// The request layer on top of queries/client.js. Provides:
//
//   - `apiJson`      — get/post/patch/put/delete helpers that THROW
//                      `ApiError` (utils/parseApiError — .status, .code,
//                      .body for structured consumers) on non-2xx instead
//                      of returning a Response.
//                      mutationFn callbacks use these so the caller never
//                      needs to write `if (!res.ok) throw new Error(...)`.
//   - `translateRequestError` — uniform i18n for a failed read or write:
//                      structured `apiErrors.<code>` key when present,
//                      caller-chosen fallback plus the next step otherwise.
//   - `readResult`   — the one shape a read hook returns: no data beside
//                      an error.
//
// All paths still go through `sessionApi` so CSRF, tenant headers, store
// scoping, and 401/403 handling stay in one place. This file only adds
// the throwing/parsing layer on top.

import { sessionApi, apiRequest as rawApiRequest } from '../services/SessionApiService';
import { apiErrorFrom, NetworkError } from '../utils/parseApiError';

/**
 * The parsed body of an accepted (2xx) response: null for an empty body
 * (a 204, or a view that answered nothing), the JSON otherwise. A non-empty
 * body that is not JSON is a broken answer, not an empty one — it throws
 * rather than hand the caller `null` as if the server had sent nothing.
 */
async function parseJsonBody(response, context) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error(
      `Response ${response.status} on ${context} is not JSON: ${err.message}`, { cause: err },
    );
  }
}

/**
 * Send a JSON request. Returns parsed JSON on 2xx (or null for 204);
 * throws `ApiError` otherwise. Used inside `useMutation.mutationFn`.
 *
 * Routes through sessionApi so all the cross-cutting concerns (CSRF,
 * tenant headers, store scoping, session-expiry hooks) stay centralized.
 */
async function requestJson(method, url, payload) {
  const lower = method.toLowerCase();
  let response;
  if (lower === 'get') {
    response = await sessionApi.get(url);
  } else if (lower === 'delete') {
    response = await sessionApi.delete(url, payload);
  } else {
    response = await sessionApi[lower](url, payload);
  }

  const context = `${method} ${url}`;
  if (!response.ok) throw await apiErrorFrom(response, context);
  if (response.status === 204) return null;
  return parseJsonBody(response, context);
}

/**
 * Upload a multipart/form-data payload (file uploads). Goes through the
 * lower-level `apiRequest` because `sessionApi.post` always JSON-stringifies
 * the body — FormData has to bypass that step so the browser can set the
 * boundary header. Returns parsed JSON on 2xx, throws `ApiError` otherwise.
 */
async function requestUpload(url, formData) {
  const response = await rawApiRequest(url, { method: 'POST', body: formData });
  const context = `POST ${url}`;
  if (!response.ok) throw await apiErrorFrom(response, context);
  if (response.status === 204) return null;
  return parseJsonBody(response, context);
}

export const apiJson = {
  get:    (url)         => requestJson('GET', url),
  post:   (url, body)   => requestJson('POST', url, body),
  patch:  (url, body)   => requestJson('PATCH', url, body),
  put:    (url, body)   => requestJson('PUT', url, body),
  delete: (url, body)   => requestJson('DELETE', url, body),
  upload: (url, formData) => requestUpload(url, formData),
};

// A refused answer with one of these statuses and no body code never came
// from our views — DRF and both DomainError paths always code their 5xx. It is
// the gateway in front of them (an Azure 502 during a deploy, a 503 while a
// slot restarts), and like a dropped connection it clears on its own.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * The i18n key of the sentence that tells the merchant what to do about a
 * failure the server gave no reason for. Three causes, three answers:
 *
 *   - the request never reached the server       → check the internet
 *                                                   connection, retry
 *   - the gateway answered for a server that was
 *     not there                                   → retry in a moment
 *   - the server refused or crashed, or our own
 *     code threw before or after the request     → contact support
 *
 * Only the first two can clear by trying again. A server that answered has
 * said what it means, and the same request a second later gets the same
 * answer (CLAUDE.md, "fallbacks cover bugs"); telling the merchant to retry
 * it hides a bug behind their patience.
 */
export function requestFailedKey(error) {
  if (error instanceof NetworkError) return 'requestFailed.network';
  if (GATEWAY_STATUSES.has(error?.status)) return 'requestFailed.gateway';
  return 'requestFailed.refused';
}

/**
 * Translate a failed request to the one sentence the merchant reads.
 *
 * A refusal that carries a structured code renders `apiErrors.<code>`: the
 * server named the rule, and that sentence says what to do about it. Any
 * other failure renders the caller's `fallbackKey` — the screen's own
 * "Failed to <do the thing>." — followed by the next step for its cause
 * (`requestFailedKey`), so the fallback text itself never says "try again".
 * Raw exception text never reaches a merchant.
 *
 * Works for writes (`mutation.error`) and reads (`query.error`) alike: the
 * query layer has already spent its one network retry on a read by the time
 * the error is rendered.
 *
 * `codeKeys` overrides the shared `apiErrors.<code>` wording for one screen,
 * for the case where the generic sentence is not merely vague but WRONG on
 * that screen (PlansPage's billing actions).
 *
 * @param {{codeKeys?: Object<string, string>}} [opts]
 */
export function translateRequestError(
  error, t, fallbackKey = 'msg.saveFailed', { codeKeys } = {},
) {
  const fallback = `${t(fallbackKey)} ${t(requestFailedKey(error))}`;
  // Duck-type on `.code` rather than `instanceof ApiError`: any error carrying
  // a structured code (ApiError, or the shared datahealth ResolveError which
  // extracts one the same way) gets the `apiErrors.<code>` translation.
  if (error?.code) {
    // The error body's own fields feed interpolation, so a message can name
    // what the merchant is looking at — "Exact Online manages your suppliers"
    // rather than "an ERP is connected". Passed under `replace` rather than
    // spread into the options object on purpose: a DomainError `extra` key
    // called `count` or `context` would otherwise land on an i18next reserved
    // option and silently change which plural form renders.
    const opts = { defaultValue: fallback, replace: error.body };
    const override = codeKeys?.[error.code];
    if (override) return t(override, opts);
    // Kept as a literal template in the t() call — audit-i18n.config.json
    // declares `apiErrors.${error.code}` and matches it by CALL SITE, so
    // folding this into the expression above silently retires the declaration
    // that enumerates every apiErrors.* key.
    return t(`apiErrors.${error.code}`, opts);
  }
  return fallback;
}

/**
 * What a read hook hands its screen: `{ data, loading, error, refetch }`,
 * with `error` already translated for an <Alert>.
 *
 * A failed refetch leaves TanStack's `query.data` holding the last answer
 * that arrived, next to the new error. A screen that checks `error` for its
 * body but reads `data` for a subtitle or a chip would then show the
 * merchant a fact from before the failure beside "Failed to load data", so
 * `data` is null whenever `error` is set.
 *
 * `fallbackKey` is the screen's own "Failed to load …" sentence, for a read
 * whose error shows away from the thing it failed to load.
 */
export function readResult(query, t, fallbackKey = 'common.fetchError') {
  return {
    data: query.isError ? null : (query.data ?? null),
    loading: query.isPending,
    error: query.isError ? translateRequestError(query.error, t, fallbackKey) : null,
    refetch: query.refetch,
  };
}
